import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { PageTitle } from "../../components/Layout";
import { missionCategory, MISSION_CATEGORIES } from "../../components/MissionPanel";
import { MissionCard } from "../../components/MissionCard";
import { EmptyState } from "../../components/Feedback";
import * as Icons from "lucide-react";
import { ICONS_BY_NAME } from "../../lib/icons";

const isValidTab = (t) => Object.prototype.hasOwnProperty.call(MISSION_CATEGORIES, t);

export default function CollabMissions() {
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const [missions, setMissions] = useState([]);
  const tab = isValidTab(params.get("tab")) ? params.get("tab") : "en_cours";
  const setTab = (t) => setParams((prev) => {
    const p = new URLSearchParams(prev);
    if (t === "en_cours") p.delete("tab"); else p.set("tab", t);
    return p;
  });
  const [search, setSearch] = useState("");
  const [fClient, setFClient] = useState("");
  const [fModule, setFModule] = useState("");
  const [sort, setSort] = useState("date_asc");

  useEffect(() => { api.get("/collab/missions").then((r) => setMissions(r.data)); }, []);

  const clients = Array.from(new Map(missions.filter((m) => m.client_id).map((m) => [m.client_id, m.client_name])).entries());
  const modules = Array.from(new Map(missions.filter((m) => m.module_key).map((m) => [m.module_key, m.module_label])).entries());

  const filtered = missions.filter((m) => {
    if (fClient && m.client_id !== fClient) return false;
    if (fModule && m.module_key !== fModule) return false;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = `${m.type} ${m.client_name || ""} ${m.project_name || ""} ${m.module_label || ""}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  const buckets = { a_venir: [], en_cours: [], terminee: [] };
  filtered.forEach((m) => buckets[missionCategory(m)].push(m));
  const list = [...buckets[tab]].sort((a, b) => {
    if (sort === "recent") return (b.created_at || "").localeCompare(a.created_at || "");
    const da = a.deadline || "", db = b.deadline || "";
    return sort === "date_desc" ? db.localeCompare(da) : da.localeCompare(db);
  });
  const hasFilters = search.trim() || fClient || fModule;

  return (
    <div>
      <PageTitle overline="Votre espace" title="Mes missions" />

      <div className="bg-sable rounded-sm border border-marine/10 p-4 mb-5" data-testid="mission-filters">
        <div className="relative mb-3">
          <Icons.Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-marine" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="mission-search" placeholder="Rechercher par mission, entreprise, projet..." className="w-full bg-creme border border-marine/15 rounded-sm pl-9 pr-3 py-2.5 text-sm text-marine focus:border-marine/50 outline-none" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select aria-label="Filtrer par entreprise" value={fClient} onChange={(e) => setFClient(e.target.value)} data-testid="mission-filter-client" className="bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none">
            <option value="">Toutes les entreprises</option>
            {clients.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          </select>
          <select aria-label="Filtrer par prestation" value={fModule} onChange={(e) => setFModule(e.target.value)} data-testid="mission-filter-module" className="bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none">
            <option value="">Tous les univers</option>
            {modules.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select>
          <select aria-label="Trier les missions" value={sort} onChange={(e) => setSort(e.target.value)} data-testid="mission-sort" className="bg-white border border-marine/15 rounded-sm px-3 py-2 text-sm text-marine focus:border-marine/50 outline-none">
            <option value="date_asc">Échéance la plus proche</option>
            <option value="date_desc">Échéance la plus lointaine</option>
            <option value="recent">Plus récentes</option>
          </select>
          {hasFilters ? <button onClick={() => { setSearch(""); setFClient(""); setFModule(""); }} data-testid="mission-filter-reset" className="text-xs px-3 py-2 text-rouge hover:underline inline-flex items-center gap-1"><Icons.X size={16} /> Réinitialiser</button> : null}
          <span className="ml-auto text-xs text-marine/80" data-testid="mission-count">{list.length} / {missions.length} mission(s)</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="mission-tabs">
        {Object.entries(MISSION_CATEGORIES).map(([key, meta]) => {
          const Ic = ICONS_BY_NAME[meta.icon] || Icons.Circle;
          const activeTab = tab === key;
          return (
            <button key={key} onClick={() => setTab(key)} data-testid={`mission-tab-${key}`}
              className={`px-4 py-1.5 rounded-sm text-sm border flex items-center gap-1.5 transition-colors ${activeTab ? "bg-marine text-white border-marine" : "border-marine/20 text-marine hover:bg-marine/5"}`}>
              <Ic size={16} style={activeTab ? {} : { color: meta.color }} /> {meta.label}
              <span className={`text-xs px-1.5 rounded-sm ${activeTab ? "bg-white/20" : "bg-sable border border-marine/30 text-marine"}`}>{buckets[key].length}</span>
            </button>
          );
        })}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="mission-grid">
        {list.map((m) => <MissionCard key={m.id} m={m} onOpen={() => nav(`/missions/${m.id}`)} />)}
        {list.length === 0 && <div className="col-span-full"><EmptyState icon="ClipboardList" title="Aucune mission ici" hint="Aucune mission ne correspond à cette catégorie ou à vos filtres actuels." /></div>}
      </div>
    </div>
  );
}
